<template>
  <div class="time-off-admin-page">
    <v-container fluid>
      <header>
        <div>
          <p>TEACHER OPERATIONS</p>
          <h1>Time-off requests</h1>
          <span>Review, print, approve, and retain every teacher request.</span>
        </div>
        <v-chip color="warning" outlined>{{ pendingCount }} pending</v-chip>
      </header>
      <v-card elevation="0" class="time-off-admin-card">
        <div class="time-off-toolbar">
          <v-text-field
            v-model="search"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            label="Search teachers"
          /><v-select
            v-model="status"
            :items="statuses"
            outlined
            dense
            hide-details
            label="Status"
          />
        </div>
        <div v-if="loading" class="time-off-state">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="filtered.length" class="time-off-table">
          <button
            v-for="request in filtered"
            :key="request.id"
            type="button"
            @click="openRequest(request)"
          >
            <span class="time-off-avatar">{{
              initials(request.Teacher && request.Teacher.name)
            }}</span>
            <span class="time-off-main"
              ><strong>{{ request.Teacher && request.Teacher.name }}</strong
              ><small
                >{{ formatReason(request.reason) }} ·
                {{ formatRange(request) }}</small
              ></span
            >
            <small class="time-off-submitted"
              >Submitted {{ formatDate(request.createdAt) }}</small
            >
            <v-chip small :color="statusColor(request.status)" outlined>{{
              request.status
            }}</v-chip>
            <v-icon color="#94a3b8">mdi-chevron-right</v-icon>
          </button>
        </div>
        <div v-else class="time-off-state">
          <v-icon size="38" color="primary">mdi-calendar-check-outline</v-icon>
          <h3>No matching requests</h3>
        </div>
      </v-card>
    </v-container>

    <v-dialog v-model="dialog" max-width="720">
      <v-card v-if="selected" class="time-off-detail">
        <div class="time-off-detail__header">
          <div>
            <p>REQUEST #{{ selected.id }}</p>
            <h2>{{ selected.Teacher && selected.Teacher.name }}</h2>
            <span>{{ formatRange(selected) }}</span>
          </div>
          <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text>
          <div class="time-off-detail__status">
            <span>Current status</span
            ><v-chip :color="statusColor(selected.status)" outlined>{{
              selected.status
            }}</v-chip>
          </div>
          <v-row
            ><v-col cols="12" sm="6"
              ><label>Reason</label
              ><strong>{{ formatReason(selected.reason) }}</strong></v-col
            ><v-col cols="12" sm="6"
              ><label>Submitted</label
              ><strong>{{ formatDate(selected.createdAt) }}</strong></v-col
            ><v-col cols="12"
              ><label>Details</label
              ><strong>{{
                selected.reasonDetails || "No additional details"
              }}</strong></v-col
            ></v-row
          >
          <v-textarea
            v-if="selected.status === 'pending'"
            v-model="decisionNote"
            outlined
            rows="3"
            label="Decision note (optional)"
            class="mt-5"
          />
          <v-alert
            v-else
            text
            :type="selected.status === 'approved' ? 'success' : 'error'"
            >{{ formatReason(selected.status) }} by
            {{ selected.DecidedByUser && selected.DecidedByUser.name }} on
            {{ formatDate(selected.decidedAt)
            }}<span v-if="selected.decisionNote"
              >: {{ selected.decisionNote }}</span
            ></v-alert
          >
          <v-alert v-if="selected.decisionEmailError" type="warning" text dense
            >The decision was saved, but the teacher email could not be
            delivered: {{ selected.decisionEmailError }}</v-alert
          >
          <div
            v-else-if="selected.decisionEmailSentAt"
            class="time-off-email-status"
          >
            <v-icon size="17" color="success">mdi-email-check-outline</v-icon
            >Email sent to the teacher
            {{ formatDate(selected.decisionEmailSentAt) }}
          </div>
        </v-card-text>
        <v-card-actions
          ><v-btn text color="primary" @click="openPdf"
            ><v-icon left>mdi-file-pdf-box</v-icon>View printable PDF</v-btn
          ><v-spacer /><template v-if="selected.status === 'pending'"
            ><v-btn
              outlined
              color="error"
              :loading="saving"
              @click="decide('rejected')"
              >Reject</v-btn
            ><v-btn
              depressed
              color="success"
              :loading="saving"
              @click="decide('approved')"
              >Approve</v-btn
            ></template
          ></v-card-actions
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import service from "./service";
export default {
  name: "teacher-time-off-admin",
  props: { id: String },
  data: () => ({
    requests: [],
    selected: null,
    dialog: false,
    loading: false,
    saving: false,
    search: "",
    status: "pending",
    decisionNote: "",
    statuses: [
      { text: "All statuses", value: "all" },
      { text: "Pending", value: "pending" },
      { text: "Approved", value: "approved" },
      { text: "Rejected", value: "rejected" },
    ],
  }),
  computed: {
    pendingCount() {
      return this.requests.filter((row) => row.status === "pending").length;
    },
    filtered() {
      const query = this.search.trim().toLowerCase();
      return this.requests.filter(
        (row) =>
          (this.status === "all" || row.status === this.status) &&
          (!query ||
            String(row.Teacher?.name || "")
              .toLowerCase()
              .includes(query))
      );
    },
  },
  async created() {
    await this.load();
    if (this.id) await this.openById(this.id);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await service.list();
        this.requests = response.data.rows || response.data || [];
      } finally {
        this.loading = false;
      }
    },
    async openById(id) {
      try {
        const response = await service.get(id);
        this.openRequest(response.data);
      } catch (error) {
        this.$swal(
          "Request unavailable",
          error?.data?.message || "This request could not be opened.",
          "error"
        );
      }
    },
    openRequest(request) {
      this.selected = request;
      this.decisionNote = "";
      this.dialog = true;
      if (String(this.$route.params.id || "") !== String(request.id))
        this.$router.replace({
          name: "teacher-time-off",
          params: { id: String(request.id) },
        });
    },
    closeDialog() {
      this.dialog = false;
      this.selected = null;
      if (this.$route.params.id)
        this.$router.replace({ name: "teacher-time-off" });
    },
    async decide(status) {
      this.saving = true;
      try {
        const response = await service.decide(this.selected.id, {
          status,
          decisionNote: this.decisionNote,
        });
        this.selected = response.data;
        await this.load();
        this.$swal({
          icon: this.selected.decisionEmailSentAt ? "success" : "warning",
          title: `${this.formatReason(status)} recorded`,
          text: this.selected.decisionEmailSentAt
            ? "The teacher has been notified by email."
            : "The decision was saved, but the email needs attention.",
        });
      } catch (error) {
        this.$swal(
          "Unable to record decision",
          error?.data?.message || "Please try again.",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async openPdf() {
      const blob = await service.pdf(this.selected.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    },
    initials(name) {
      return String(name || "T")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },
    formatDate(value) {
      return this.$moment(value).format("MMM D, YYYY");
    },
    formatReason(value) {
      return String(value || "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatRange(row) {
      return row.requestType === "hours"
        ? `${this.formatDate(row.startDate)}, ${String(row.startTime).slice(
            0,
            5
          )}-${String(row.endTime).slice(0, 5)}`
        : `${this.formatDate(row.startDate)}${
            row.endDate !== row.startDate
              ? ` - ${this.formatDate(row.endDate)}`
              : ""
          }`;
    },
    statusColor(value) {
      return value === "approved"
        ? "success"
        : value === "rejected"
        ? "error"
        : "warning";
    },
  },
};
</script>

<style scoped>
.time-off-admin-page {
  min-height: 100%;
  padding: 28px 34px 50px;
  background: #f6f8f6;
}
.time-off-admin-page .container {
  max-width: 1400px;
  padding: 0;
}
.time-off-admin-page header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 22px;
}
.time-off-admin-page header p,
.time-off-detail__header p {
  margin: 0 0 5px;
  color: #43a047;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
}
.time-off-admin-page h1 {
  margin: 0;
  color: #172033;
  font-size: 29px;
}
.time-off-admin-page header span {
  color: #728095;
}
.time-off-admin-card {
  overflow: hidden;
  border: 1px solid #e3e9e3;
  border-radius: 13px !important;
}
.time-off-toolbar {
  display: flex;
  gap: 14px;
  padding: 18px;
  border-bottom: 1px solid #e8ede8;
}
.time-off-toolbar .v-text-field {
  max-width: 520px;
}
.time-off-toolbar .v-select {
  max-width: 210px;
}
.time-off-table button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid #edf1ed;
  text-align: left;
}
.time-off-table button:hover {
  background: #f8faf8;
}
.time-off-avatar {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eaf6eb;
  color: #2f7d4a;
  font-weight: 700;
}
.time-off-main {
  display: flex;
  min-width: 260px;
  flex: 1;
  flex-direction: column;
}
.time-off-main small,
.time-off-submitted {
  color: #7b8799;
}
.time-off-state {
  display: flex;
  min-height: 230px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.time-off-detail {
  overflow: hidden;
  border-radius: 14px !important;
}
.time-off-detail__header {
  display: flex;
  justify-content: space-between;
  padding: 22px 24px;
  background: #f4f8f4;
}
.time-off-detail__header h2 {
  margin: 0;
  color: #172033;
}
.time-off-detail__header span {
  color: #728095;
}
.time-off-detail .v-card__text {
  padding: 24px;
}
.time-off-detail label {
  display: block;
  margin-bottom: 4px;
  color: #7b8799;
  font-size: 11px;
  text-transform: uppercase;
}
.time-off-detail strong {
  color: #253047;
}
.time-off-detail__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #edf1ed;
}
.time-off-detail .v-card__actions {
  padding: 12px 20px 20px;
}
.time-off-email-status {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #587063;
  font-size: 12px;
}
@media (max-width: 600px) {
  .time-off-admin-page {
    padding: 20px 12px 36px;
  }
  .time-off-toolbar {
    flex-direction: column;
  }
  .time-off-toolbar .v-text-field,
  .time-off-toolbar .v-select {
    max-width: none;
  }
  .time-off-submitted {
    display: none;
  }
  .time-off-main {
    min-width: 0;
  }
}
</style>
